/**
 * TeamImageExport — Full team layout for PNG export.
 *
 * This component is both the visual preview in the modal AND the
 * capture target for html-to-image (toPng). A ref is forwarded so
 * the parent can pass ref.current to the export pipeline.
 *
 * Design based on /dexbuilder reference.
 * All inline styles for reliable cross-browser capture.
 */
import React, { forwardRef } from 'react';
import ExportCard from './ExportCard';

const TeamImageExport = forwardRef(
  ({ team, teamName, teamNotes, language }, ref) => {
    const formattedDate = new Date().toLocaleDateString(
      language === 'es' ? 'es-ES' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric' },
    );
    const teamCount = (team || []).filter(s => s?.data).length;
    const logoUrl = `${window.location.origin}/logo-dexriddle-transparent.png`;

    return (
      <div
        ref={ref}
        style={{
          width: 920,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
          padding: '24px 24px 16px 24px',
          color: '#fff',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          opacity: 0.04,
          backgroundImage: [
            'radial-gradient(circle at 15% 25%, #7C3AED 0%, transparent 45%)',
            'radial-gradient(circle at 85% 75%, #EC4899 0%, transparent 45%)',
          ].join(', '),
          pointerEvents: 'none',
        }} />

        {/* HEADER */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Left: Logo + Team Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={logoUrl}
              alt="DexRiddle"
              crossOrigin="anonymous"
              loading="eager"
              decoding="sync"
              onError={e => { e.target.style.display = 'none'; }}
              style={{
                height: 32,
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            {teamName && (
              <span style={{
                fontSize: 18,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #A78BFA 0%, #EC4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1,
              }}>
                {teamName}
              </span>
            )}
          </div>

          {/* Right: Date + Count */}
          <span style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.45)',
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}>
            {formattedDate} &bull; {teamCount}/6
          </span>
        </div>

        {/* TEAM GRID: 2 columns x 3 rows */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          position: 'relative',
          zIndex: 1,
        }}>
          {[0, 1, 2, 3, 4, 5].map(idx => (
            <ExportCard
              key={idx}
              slot={(team || [])[idx]}
              language={language}
              index={idx}
            />
          ))}
        </div>

        {/* FOOTER */}
        <div style={{
          marginTop: 14,
          paddingTop: 8,
          borderTop: '1px solid rgba(168, 85, 247, 0.18)',
          textAlign: 'center',
          fontSize: 10,
          color: 'rgba(255,255,255,0.22)',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          position: 'relative',
          zIndex: 1,
        }}>
          DexRiddle
        </div>
      </div>
    );
  }
);

TeamImageExport.displayName = 'TeamImageExport';

export default TeamImageExport;
